/* --------------- *** Overlay Arrows *** --------------- */

/**
 * Vertically centers overlay carousel arrows on the media height
 * rather than the full card height (which includes title/text below).
 *
 * Each container exposes `--overlay-arrow-center` (half the media height),
 * which the stylesheet uses as the arrows' inset-block-start.
 *
 * A ResizeObserver keeps the value in sync after the slider relayouts on
 * resize/breakpoint changes, avoiding races with the swiper's own resize.
 */
(() => {
  const INIT_FLAG = '__overlayArrowsInit';

  // Each section injects this script, so the tag can appear several times on a
  // page. Guard against re-running so we don't stack duplicate observers and
  // listeners across those copies.
  if (window[INIT_FLAG]) return;
  window[INIT_FLAG] = true;

  const CONTAINER_SELECTOR = '.has-overlay-arrows';
  const MEDIA_SELECTOR = '.media';
  const CENTER_PROPERTY = '--overlay-arrow-center';

  const observed = new WeakSet();

  // Find the first laid-out (visible) media element within a container.
  const getVisibleMedia = (container) => {
    const mediaElements = Array.from(container.querySelectorAll(MEDIA_SELECTOR));

    return mediaElements.find((media) => media.offsetHeight > 0);
  };

  const positionArrows = (container) => {
    const media = getVisibleMedia(container);

    if (!media) return;

    container.style.setProperty(CENTER_PROPERTY, `${media.offsetHeight / 2}px`);
  };

  // Observe a container so its arrows recenter whenever its media resizes.
  const observeContainer = (container, resizeObserver) => {
    if (observed.has(container)) {
      positionArrows(container);
      return;
    }

    observed.add(container);
    resizeObserver.observe(container);
    positionArrows(container);
  };

  const init = () => {
    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => positionArrows(entry.target));
    });

    const observeAll = () => {
      document
        .querySelectorAll(CONTAINER_SELECTOR)
        .forEach((container) => observeContainer(container, resizeObserver));
    };

    /* --------------- *** Event Listeners *** --------------- */

    // Re-scan when a section is re-rendered inside the theme editor.
    document.addEventListener('shopify:section:load', observeAll);

    observeAll();
  };

  init();
})();
